export { SepoliaChain, SepoliaChainReadOnly } from "./Chains";
export { KeyserverStorage } from "./Storage";
export * from "./Client";

declare global {
  // Core-js polyfil: https://github.com/zloirock/core-js/blob/master/packages/core-js/modules/esnext.uint8-array.to-base64.js
  // Also see: https://github.com/zloirock/core-js?tab=readme-ov-file#uint8array-to--from-base64-and-hex
  interface Uint8Array {
    toBase64(options?: {
      alphabet?: "base64" | "base64url";
      omitPadding?: boolean;
    }): string;
  }
  interface Uint8ArrayConstructor {
    fromBase64(
      string: string,
      options?: {
        alphabet?: "base64" | "base64url";
        lastChunkHandling?: "loose" | "strict" | "stop-before-partial";
      },
    ): Uint8Array;
  }
}

export type State<S> = [S, (v: S) => void];

export function arrayEntryAsState<S>(
  [value, setValue]: State<S[]>,
  index: number,
): State<S | null> {
  return [
    value[index],
    (v) => {
      const out = [...value];
      if (v === null) {
        delete out[index];
      } else {
        out[index] = v;
      }
      setValue(out);
    },
  ];
}

// NOTE: I don't think we can do these functions without using any. The type
// definition for that would probably get ridiculous (likely not worth it).
// NOTE: This means the caller is responsible for ensuring state contains
// something of type S at path. If that isn't the case this function won't crash
// but it wont return the type specified here.
export function pathAsState<S>(state: State<any>, path: string): State<S> {
  return [
    getInObject(state, path),
    (value) => {
      setInObject(state, path, value);
    },
  ];
}

// Convenience function if we only want one part.
export function setInObject(state: State<any>, path: string, value: any) {
  const [obj, setObj] = state;
  setObj(setInObjectCopy(obj, path, value));
}
export function getInObject<S>(state: State<any>, path: string): S {
  let current: any = state[0];
  path.split(".").forEach((p) => {
    current = current[p];
  });
  return current;
}

// Useful/Needed in react because it doesn't like us modifying objects.
// NOTE: We may have to rewrite this to be a resursive function at some point to
// detect objects that have become empty.
export function setInObjectCopy(obj: any, path: string, value: any): any {
  const ret = { ...obj };
  let current_out = ret;
  let current_in = obj;
  const paths = path.split(".");
  paths.forEach((p, i) => {
    if (i === paths.length - 1) {
      if (value !== null) {
        current_out[p] = value;
      } else {
        // TODO: If this would create an empty object/array: Remove the parents
        // (not easy).
        delete current_out[p];
      }
    } else {
      // We need to create a copy on each step (stupid React immutability).
      if (Array.isArray(current_in[p])) {
        current_out[p] = [...current_in[p]];
      } else {
        current_out[p] = { ...current_in[p] };
      }
      current_out = current_out[p];
      current_in = current_in[p];
    }
  });
  return ret;
}

export function ifString(value: any): string | undefined {
  if (typeof value === "string") {
    return value;
  }
  return undefined;
}

export function ifFileOrUrl(value: any): string | File | undefined {
  if (typeof value === "string" || value instanceof File) {
    return value;
  }
  return undefined;
}
